import { useEffect, useState } from 'react';
import { CachedResource } from '../utils/cachedResource';

export interface ResourceState<T> {
  data: T | undefined;
  loading: boolean;
  error: boolean;
}

/** Egy közös `cachedResource` állapota komponensben (egyetlen kérés az egész oldalon). */
export function useCachedResource<T>(resource: CachedResource<T>): ResourceState<T> {
  const [state, setState] = useState<ResourceState<T>>(() => {
    const cached = resource.peek();
    return { data: cached, loading: cached === undefined, error: false };
  });

  useEffect(() => {
    // Ha az első render és az effect között (pl. egy másik komponens kérése
    // révén) megjött az érték, azt vegyük át — különben a példány „tölt”
    // állapotban ragadna.
    const cached = resource.peek();
    if (cached !== undefined) {
      setState((prev) => (prev.data === cached && !prev.loading ? prev : { data: cached, loading: false, error: false }));
      return undefined;
    }
    let alive = true;
    resource
      .get()
      .then((data) => {
        if (alive) setState({ data, loading: false, error: false });
      })
      .catch(() => {
        if (alive) setState({ data: undefined, loading: false, error: true });
      });
    return () => {
      alive = false;
    };
  }, [resource]);

  return state;
}
